import {
  isEnglishMonarchLocale,
  normalizeMonarchLocale,
} from "./normalizeMonarchLocale";

/** Locale code → translated string, e.g. `{ es: "..." }`. */
export type MonarchLocaleLeaf = Record<string, string>;

/**
 * Tree-shaped localization map under a component's root `localization`.
 * Leaves are locale maps; branches mirror nested object/array field ids.
 */
export type MonarchLocalizationMap = {
  [key: string]: MonarchLocalizationNode;
};

export type MonarchLocalizationNode =
  | MonarchLocaleLeaf
  | MonarchLocalizationMap
  | MonarchLocalizationNode[];

type LocalizableRecord = Record<string, unknown> & {
  localization?: MonarchLocalizationMap;
};

function isLocalizableRecord(value: unknown): value is LocalizableRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** True when every value is a string (locale → translation). */
function isLocaleLeaf(value: unknown): value is MonarchLocaleLeaf {
  if (!isLocalizableRecord(value)) {
    return false;
  }

  const values = Object.values(value);
  return (
    values.length > 0 && values.every((entry) => typeof entry === "string")
  );
}

function isLocalizationBranch(value: unknown): value is MonarchLocalizationMap {
  return isLocalizableRecord(value) && !isLocaleLeaf(value);
}

function resolveMonarchLocalizationMap(
  record: LocalizableRecord,
): MonarchLocalizationMap | undefined {
  if (record.localization) {
    return record.localization;
  }

  const nestedData = record.data;
  if (isLocalizableRecord(nestedData) && nestedData.localization) {
    return nestedData.localization;
  }

  return undefined;
}

function walkLocalizedEntries(
  value: unknown,
  locale: string,
  localization: MonarchLocalizationMap | undefined,
): unknown {
  if (!isLocalizableRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => {
      if (key === "localization") {
        return [key, entryValue];
      }

      const localizationEntry = localization?.[key];

      if (typeof entryValue === "string") {
        if (isLocaleLeaf(localizationEntry)) {
          return [key, localizationEntry[locale] ?? entryValue];
        }
        return [key, entryValue];
      }

      if (Array.isArray(entryValue)) {
        if (Array.isArray(localizationEntry)) {
          return [
            key,
            entryValue.map((item, index) => {
              const itemLocalization = localizationEntry[index];
              if (typeof item === "string" && isLocaleLeaf(itemLocalization)) {
                return itemLocalization[locale] ?? item;
              }
              if (isLocalizationBranch(itemLocalization)) {
                return walkLocalizedEntries(item, locale, itemLocalization);
              }
              // Missing index or non-branch/non-leaf entry → English pass-through
              return item;
            }),
          ];
        }

        // No array branch → English pass-through so sibling locale leaves
        // (e.g. module `title`) do not rewrite item fields like bullet titles.
        return [key, entryValue];
      }

      if (isLocalizableRecord(entryValue)) {
        if (isLocalizationBranch(localizationEntry)) {
          return [
            key,
            walkLocalizedEntries(entryValue, locale, localizationEntry),
          ];
        }

        // Missing nested branch: continue with the current map so unique flat
        // keys still reach nested objects (e.g. `data.cta` + root `cta` leaf).
        return [key, walkLocalizedEntries(entryValue, locale, localization)];
      }

      return [key, entryValue];
    }),
  );
}

export function withMonarchTranslations<T>(data: T, locale: string): T {
  if (isEnglishMonarchLocale(locale) || !isLocalizableRecord(data)) {
    return data;
  }

  const localization = resolveMonarchLocalizationMap(data);
  if (!localization) {
    return data;
  }

  const normalizedLocale = normalizeMonarchLocale(locale);

  return walkLocalizedEntries(data, normalizedLocale, localization) as T;
}
